import dayjs from 'dayjs';

import type { ExtranetHotelExperience } from 'app/typings/core/extranet/hotel/experiences';
import type { ExtranetHotelPack } from 'app/typings/core/extranet/hotel/packs';
import { ROOM_OPENING_DISCOUNT_MODE } from 'app/typings/core/extranet/inventory/enums/RoomOpeningDiscountMode';
import type {
  InventoryPack,
  InventoryResponse,
} from 'app/typings/core/extranet/inventory/inventory';
import { formatDateWithoutHour } from 'app/utils/dates';
import { addDays } from 'app/utils/dayjs';

import type { ExperienceOpening } from './experiences';
import { computeExperienceOnSingleDate } from './experiences';
import type { ComputePacksResult, PackDetails, PackRoomDetails } from './packs';
import {
  PackType,
  computePackOnSingleDate,
  getRoomOpeningsForPack,
} from './packs';
import type { RoomOpeningInventory } from './rooms';
import { computeRoomOnSingleDate } from './rooms';

// Types specific to inventory updates
export type InventoryUpdateDependencies = {
  saleEndDate: Date;
  packData: ExtranetHotelPack[];
  filteredExperiences: ExtranetHotelExperience[];
  inventoryInput: InventoryResponse;
  hasChannelManager: boolean;
  hasAutoInventory: boolean;
  defaultNightRoomDiscount?: number | null;
  lastMinuteNightRoomDiscountRate?: number | null;
  lastMinuteNightRoomDiscountDays?: number | null;
};

const isInLastMinuteWindow = (
  date: string,
  lastMinuteNightRoomDiscountRate: number | null | undefined,
  lastMinuteNightRoomDiscountDays: number | null | undefined
): boolean => {
  if (
    lastMinuteNightRoomDiscountRate === undefined ||
    lastMinuteNightRoomDiscountRate === null ||
    lastMinuteNightRoomDiscountDays === undefined ||
    lastMinuteNightRoomDiscountDays === null ||
    lastMinuteNightRoomDiscountDays <= 0
  ) {
    return false;
  }

  const dateObj = dayjs(date);

  return (
    dateObj.isBefore(
      dayjs().add(lastMinuteNightRoomDiscountDays + 1, 'days'),
      'days'
    ) && !dateObj.isBefore(dayjs(), 'days')
  );
};

const computeDiscountPrice = (price: number, rate: number): number =>
  Math.ceil(Math.round(price * (1 - rate) * 1e6) / 1e6);

type RoomUpdate = {
  remainingStock?: number | null;
  price?: number | null;
  discountPrice?: number | null;
  discount?: number | null;
  isClosedByStaycation?: boolean;
  isClosedByHotel?: boolean;
};

// Recomputes a pack for a specific date using current result data
const recomputePackForDate = (
  packData: ExtranetHotelPack,
  date: Date,
  dateKey: string,
  currentResult: ComputePacksResult,
  saleEndDate: Date,
  filteredExperiences: ExtranetHotelExperience[],
  inventoryInput: InventoryResponse
): PackDetails => {
  const packOpening = inventoryInput[dateKey]?.packs[packData.id];

  // Get room openings for the date
  const roomOpeningsForDate = currentResult[dateKey]?.rooms ?? {};

  // For night stay packs, we need experience openings for both the check-in date and check-out date (day after)
  const nextDayDate = addDays(date, 1);
  const nextDayKey = formatDateWithoutHour(nextDayDate);

  const experienceOpeningsForDay = currentResult[dateKey]?.experiences ?? {};
  const experienceOpeningsForNextDay =
    currentResult[nextDayKey]?.experiences ?? {};

  // Merge experience openings from both dates for night stay calculation
  const mergedExperienceOpenings: Record<
    string,
    Record<number, ExperienceOpening>
  > = {
    [dateKey]: experienceOpeningsForDay,
    [nextDayKey]: experienceOpeningsForNextDay,
  };

  // Get room openings for the pack
  const roomOpeningsForPack = getRoomOpeningsForPack(
    packData,
    roomOpeningsForDate
  );

  // Convert array to record for compatibility
  const roomOpeningsRecord = roomOpeningsForPack.reduce(
    (acc, opening) => {
      acc[opening.roomId] = opening;

      return acc;
    },
    {} as Record<number, RoomOpeningInventory>
  );

  const visibleExperienceIds = new Set(
    filteredExperiences.map((exp) => exp.id)
  );

  // Recompute pack details
  return computePackOnSingleDate(
    saleEndDate,
    date,
    packData,
    filteredExperiences,
    roomOpeningsRecord,
    mergedExperienceOpenings,
    packOpening,
    visibleExperienceIds
  );
};

// Finds existing pack opening or creates/updates one for maxBooking changes
const getOrUpdatePackOpening = (
  packId: number,
  date: string,
  maxBooking: number | null,
  inventoryInput: InventoryResponse
): InventoryResponse => {
  const existingOpening = inventoryInput[date]?.packs[packId];

  if (existingOpening) {
    // Update existing opening
    if (maxBooking === null) {
      // Remove maxBooking by setting it to undefined
      existingOpening.maxBooking = undefined;
    } else {
      existingOpening.maxBooking = maxBooking;
    }

    return {
      ...inventoryInput,
      [date]: {
        ...inventoryInput[date],
        packs: {
          ...inventoryInput[date]?.packs,
          [packId]: existingOpening,
        },
      },
    };
  }

  // If no opening exists and we're setting maxBooking, create one
  if (maxBooking !== null) {
    const newOpening: InventoryPack = {
      maxBooking,
      isMaxBookingOverridden: false,
      bookedPack: 0,
    };

    return {
      ...inventoryInput,
      [date]: {
        ...inventoryInput[date],
        packs: {
          ...inventoryInput[date]?.packs,
          [packId]: newOpening,
        },
      },
    };
  }

  return inventoryInput;
};

/**
 * Updates the maxBooking for a specific pack on a specific date and recomputes
 * only that pack for that date, avoiding a full recomputation.
 */
export const updateMaxBookingForDate = (
  currentResult: ComputePacksResult,
  packId: number,
  date: string,
  maxBooking: number | null,
  dependencies: InventoryUpdateDependencies
): ComputePacksResult => {
  const dateKey = date;
  const dateObj = new Date(date);

  // Find the pack data
  const packData = dependencies.packData.find((pkg) => pkg.id === packId);

  if (!packData) {
    return currentResult;
  }

  // Find or create the pack opening for this date (mutates packOpenings array)
  const updatedInventoryInput = getOrUpdatePackOpening(
    packId,
    date,
    maxBooking,
    dependencies.inventoryInput
  );

  // Recompute pack for this date
  const updatedPackDetails = recomputePackForDate(
    packData,
    dateObj,
    dateKey,
    currentResult,
    dependencies.saleEndDate,
    dependencies.filteredExperiences,
    updatedInventoryInput
  );

  const updatedPacks = {
    ...currentResult[dateKey]?.packs,
    [packId]: updatedPackDetails,
  };

  // Return a new result object with only the updated pack/date changed
  return {
    ...currentResult,
    [dateKey]: {
      packs: updatedPacks,
      rooms: currentResult[dateKey]?.rooms ?? {},
      experiences: currentResult[dateKey]?.experiences ?? {},
    },
  };
};

// Updates experience stock for a date and recomputes affected packs (optimized)
export const updateExperienceStockForDate = (
  currentResult: ComputePacksResult,
  experienceId: number,
  date: string,
  remainingStock: number | null,
  dependencies: InventoryUpdateDependencies
): ComputePacksResult => {
  const dateKey = date;
  const dateObj = new Date(date);

  // Find the experience
  const extranetExperience = dependencies.filteredExperiences.find(
    (exp) => exp.id === experienceId
  );

  if (!extranetExperience) {
    return currentResult;
  }

  const inventoryExperience =
    dependencies.inventoryInput[dateKey]?.experiences[experienceId];

  if (inventoryExperience === undefined) {
    return currentResult;
  }

  const updatedExperience = {
    ...inventoryExperience,
    remainingStock: remainingStock,
  };

  // Recompute the experience opening for this date
  const updatedExperienceOpening = computeExperienceOnSingleDate(
    dependencies.saleEndDate,
    dateObj,
    extranetExperience,
    updatedExperience
  );

  return {
    ...currentResult,
    [dateKey]: {
      ...currentResult[dateKey],
      experiences: {
        ...currentResult[dateKey]?.experiences,
        [experienceId]: updatedExperienceOpening,
      },
    },
  };
};

/**
 * Updates room fields (remainingStock, price, discountPrice, discount) for a specific room
 * on a specific date and recomputes affected packs.
 *
 * Note: remainingStock and price are computed fields. Updating remainingStock adjusts stock,
 * and updating price adjusts discount based on the current price.
 */
export const updateRoomFieldsForDate = (
  currentResult: ComputePacksResult,
  roomId: number,
  date: string,
  updates: RoomUpdate,
  dependencies: InventoryUpdateDependencies
): ComputePacksResult => {
  const dateKey = date;
  const dateObj = new Date(date);

  // Find the room opening in the source data
  const roomOpening = dependencies.inventoryInput[dateKey]?.rooms[roomId];
  const currentOpeninResult = currentResult[dateKey]?.rooms[roomId];

  if (!roomOpening) {
    return currentResult;
  }

  if (updates.isClosedByHotel !== undefined) {
    roomOpening.isClosedByHotel = updates.isClosedByHotel;
  }

  // Update remainingStock by adjusting stock (remainingStock = stock - booked)
  if (updates.remainingStock !== undefined) {
    roomOpening.remainingStock = updates.remainingStock ?? undefined;
    // A manual stock edit deactivates auto-inventory for the stock badge.
    roomOpening.isStockAutomated = false;
  }

  // Discount currently displayed for this opening (before this edit).
  const previousDiscount = currentOpeninResult?.discount;

  // Whether the default / last-minute discount rule still drives this opening.
  const isAutoDiscountMode =
    roomOpening.discountMode === ROOM_OPENING_DISCOUNT_MODE.AUTO ||
    roomOpening.discountMode === ROOM_OPENING_DISCOUNT_MODE.LAST_MINUTE_AUTO;

  // Discount value forwarded to computeRoomOnSingleDate.
  // `undefined` => let it recompute the discount from price/discountPrice.
  let nextDiscount: number | null | undefined = previousDiscount;

  // Update price first if provided (needed for discountPrice calculation)
  if (updates.price !== undefined) {
    roomOpening.price = updates.price ?? undefined;
    // A manual BAR edit deactivates auto-inventory for the price badge.
    roomOpening.isPriceAutomated = false;

    if (roomOpening.price === undefined) {
      // BAR cleared: the discount can no longer be derived from price/RRS. Keep
      // the current discount and RRS in place rather than wiping them.
      nextDiscount = previousDiscount;
    } else if (isAutoDiscountMode) {
      // Auto mode: keep the discount already displayed, otherwise seed it from
      // the hotel pricing rule (default / last-minute rate) so the discount and
      // the room revenue share (RRS) are computed as soon as the BAR is set.
      const autoRate =
        roomOpening.discountMode === ROOM_OPENING_DISCOUNT_MODE.LAST_MINUTE_AUTO
          ? dependencies.lastMinuteNightRoomDiscountRate
          : dependencies.defaultNightRoomDiscount;
      const autoDiscount = previousDiscount ?? autoRate ?? undefined;

      if (autoDiscount !== undefined && autoDiscount !== null) {
        roomOpening.discountPrice = computeDiscountPrice(
          roomOpening.price,
          autoDiscount
        );
        nextDiscount = autoDiscount;
      } else {
        nextDiscount = undefined;
      }
    } else if (roomOpening.discountMode === ROOM_OPENING_DISCOUNT_MODE.MANUAL) {
      // Manual mode: the % is frozen, so editing the BAR recomputes the RRS from
      // the stored rate, capped at the BAR.
      // A `null` rate falls back to `undefined` (and not to the displayed
      // discount): the back does not recompute the RRS when it receives
      // `rate: null` (cf. computeDiscountInfo), so recomputing it here would
      // make the front diverge from the back once the data is saved and the
      // cache invalidated.
      const manualRate = roomOpening.discountRate ?? undefined;

      if (manualRate !== undefined) {
        roomOpening.discountPrice = Math.min(
          computeDiscountPrice(roomOpening.price, manualRate),
          roomOpening.price
        );
        nextDiscount = manualRate;
      } else {
        nextDiscount = undefined;
      }
    } else if (roomOpening.discountPrice === undefined) {
      const isLastMinute = isInLastMinuteWindow(
        dateKey,
        dependencies.lastMinuteNightRoomDiscountRate,
        dependencies.lastMinuteNightRoomDiscountDays
      );
      const ruleRate = isLastMinute
        ? dependencies.lastMinuteNightRoomDiscountRate
        : dependencies.defaultNightRoomDiscount;

      if (ruleRate !== undefined && ruleRate !== null) {
        roomOpening.discountMode = isLastMinute
          ? ROOM_OPENING_DISCOUNT_MODE.LAST_MINUTE_AUTO
          : ROOM_OPENING_DISCOUNT_MODE.AUTO;
        roomOpening.discountPrice = computeDiscountPrice(
          roomOpening.price,
          ruleRate
        );
        nextDiscount = ruleRate;
      } else {
        nextDiscount = undefined;
      }
    } else {
      // NONE mode: editing the BAR keeps the RRS and recomputes the discount.
      nextDiscount = undefined;
    }
  }

  // Editing the RRS freezes the room revenue share (NONE): the % is derived from
  // it and the stored discount rate is cleared.
  if (updates.discountPrice !== undefined) {
    roomOpening.discountPrice = updates.discountPrice ?? undefined;
    roomOpening.discountMode = ROOM_OPENING_DISCOUNT_MODE.NONE;
    roomOpening.discountRate = null;
    nextDiscount = undefined;
  }

  // Editing the discount freezes the % (MANUAL): the rate is stored and the RRS
  // is recomputed from it (discountPrice = ceil(price × (1 - rate))).
  if (updates.discount !== undefined && updates.discount !== null) {
    const currentPrice = roomOpening.price;

    if (currentPrice !== undefined) {
      roomOpening.discountPrice = computeDiscountPrice(
        currentPrice,
        updates.discount
      );
    }

    roomOpening.discountMode = ROOM_OPENING_DISCOUNT_MODE.MANUAL;
    roomOpening.discountRate = updates.discount;
    nextDiscount = updates.discount;
  }

  if (updates.isClosedByStaycation !== undefined) {
    roomOpening.isClosedByStaycation = updates.isClosedByStaycation;
  }

  // Recompute the room opening inventory
  const updatedRoomOpeningInventory = computeRoomOnSingleDate(
    dependencies.saleEndDate,
    dateObj,
    roomId,
    roomOpening,
    dependencies.hasChannelManager,
    dependencies.hasAutoInventory,
    nextDiscount
  );

  // Find all packs that include this room
  const affectedPacks = dependencies.packData.filter((pkg) =>
    pkg.packRooms.some((room) => room.roomId === roomId)
  );

  if (affectedPacks.length === 0) {
    // No packs affected, just update the room opening
    return {
      ...currentResult,
      [dateKey]: {
        ...currentResult[dateKey],
        rooms: {
          ...currentResult[dateKey]?.rooms,
          [roomId]: updatedRoomOpeningInventory,
        },
      },
    };
  }

  // Store all pack updates by date key
  const updatedPacksByDate: Record<string, Record<number, PackDetails>> = {
    [dateKey]: {
      ...currentResult[dateKey]?.packs,
    },
  };

  // Update room openings for the date with the updated room
  const updatedRoomsForDate: Record<number, RoomOpeningInventory> = {
    ...currentResult[dateKey]?.rooms,
    [roomId]: updatedRoomOpeningInventory,
  };

  // Create a modified currentResult with updated room for recomputation
  const modifiedCurrentResult: ComputePacksResult = {
    ...currentResult,
    [dateKey]: {
      ...currentResult[dateKey],
      rooms: updatedRoomsForDate,
    },
  };

  // Recompute affected packs for this date
  affectedPacks.forEach((packData) => {
    updatedPacksByDate[dateKey][packData.id] = recomputePackForDate(
      packData,
      dateObj,
      dateKey,
      modifiedCurrentResult,
      dependencies.saleEndDate,
      dependencies.filteredExperiences,
      dependencies.inventoryInput
    );
  });

  Object.entries(updatedPacksByDate[dateKey]).forEach(([packId, cp]) => {
    if (cp.type === PackType.WITHOUT_ROOM) {
      return;
    }

    cp.roomsDetails.forEach((rd: PackRoomDetails) => {
      // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
      const roomOpening = updatedRoomsForDate[rd.roomId];

      if (!roomOpening) {
        return;
      }

      roomOpening.packsDetails = roomOpening.packsDetails ?? [];
      roomOpening.packsDetails.push({
        packId: parseInt(packId, 10),
        totalPrice: rd.totalPrice,
        discountedTotalPrice: rd.discountedTotalPrice,
        discount: rd.discount,
      });
    });
  });

  // Build result with updated packs and rooms
  const result: ComputePacksResult = {
    ...currentResult,
    [dateKey]: {
      packs: updatedPacksByDate[dateKey],
      rooms: updatedRoomsForDate,
      experiences: currentResult[dateKey]?.experiences ?? {},
    },
  };

  return result;
};
